import React, { useState, useEffect, useCallback } from 'react';
import friendlyError from '../../utils/friendlyError';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FaChessKnight, FaSyncAlt, FaBolt, FaShieldAlt, FaMagic, FaExclamationTriangle, FaArrowRight } from 'react-icons/fa';
import {
  TechShell, TechPageHeader, TechSection, TechTabs, TechInput, TechSelect, TechButton, TechButtonGhost,
  TechChip, StatusPill, StatStrip, StatTile, StatLabel, StatValue, StatSub, Empty, Loading, ErrorBox, borderSoft, mono, accent,
} from '../../styles/techComponents';
import { getNarrativa, getPrep, getWarroom } from '../../services/estrategiaService';

// A qué sección lleva cada tipo de acción.
const SECCION_ROUTE = {
  Territorio: '/territorio', Prensa: '/prensa', Contenido: '/messaging',
  Estrategia: '/estrategia', HCD: '/labor-hcd', Electoral: '/electoral',
};

const Card = styled.div` border:1px solid ${borderSoft};border-radius:8px;padding:0.8rem 0.9rem;margin-bottom:0.6rem;background:rgba(11,19,32,0.4);
  h5{margin:0 0 0.3rem;font-size:0.92rem} p{margin:0.2rem 0;font-size:0.86rem;color:var(--text-secondary)} `;
const Prep = styled.pre` white-space:pre-wrap;font-family:${mono};font-size:0.82rem;line-height:1.55;color:var(--text-primary);
  border:1px solid ${borderSoft};border-radius:8px;padding:1rem;background:rgba(11,19,32,0.4);overflow-x:auto; `;
const Lbl = styled.label` font-family:${mono};font-size:0.64rem;letter-spacing:0.1em;text-transform:uppercase;color:var(--text-secondary);display:block;margin-bottom:0.2rem; `;

function Narrativa() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getNarrativa()); } catch (e) { setError(friendlyError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Loading>Analizando la narrativa rival…</Loading>;
  if (error) return <ErrorBox><FaExclamationTriangle /> {error}</ErrorBox>;
  if (data?.sin_datos) return <Empty>{data.mensaje}</Empty>;
  const a = data?.analisis || {};

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
        <StatusPill>{data?.notas_analizadas || 0} notas · {data?.ventana_dias || 30}d</StatusPill>
        <TechButtonGhost onClick={load}><FaSyncAlt /> Re-analizar</TechButtonGhost>
      </div>
      <TechSection title="Qué empujan los rivales" icon={<FaBolt />}>
        {(a.temas_rival || []).map((t, i) => (
          <Card key={i}><h5>{t.tema} <TechChip>{t.fuerza}</TechChip></h5><p>{t.que_dicen}</p></Card>
        ))}
        {(a.temas_rival || []).length === 0 && <Empty>Sin temas detectados.</Empty>}
      </TechSection>
      <TechSection title="Vulnerabilidades del rival" icon={<FaShieldAlt />}>
        {(a.vulnerabilidades || []).map((v, i) => <Card key={i}><p>🎯 {v}</p></Card>)}
      </TechSection>
      <TechSection title="Tu contra-mensaje" icon={<FaChessKnight />}>
        {(a.contra_mensajes || []).map((c, i) => (
          <Card key={i}><h5 style={{ color: accent }}>{c.sobre}</h5><p>{c.angulo_chouhy}</p></Card>
        ))}
      </TechSection>
    </div>
  );
}

function Preparador() {
  const [form, setForm] = useState({ tipo: 'sesion', tema: '', medio: '' });
  const [out, setOut] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  async function run() {
    if (!form.tema.trim()) return;
    setBusy(true); setErr(null); setOut(null);
    try { const r = await getPrep(form); setOut(r.prep); } catch (e) { setErr(friendlyError(e)); } finally { setBusy(false); }
  }
  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: '0.5rem', alignItems: 'end', marginBottom: '0.6rem' }}>
        <div><Lbl>Tipo</Lbl><TechSelect value={form.tipo} onChange={(e) => setForm({ ...form, tipo: e.target.value })}><option value="sesion">Sesión HCD</option><option value="entrevista">Entrevista</option><option value="debate">Debate</option></TechSelect></div>
        <div><Lbl>Tema</Lbl><TechInput placeholder="Ej: seguridad en San Nicolás" value={form.tema} onChange={(e) => setForm({ ...form, tema: e.target.value })} /></div>
        <TechButton onClick={run} disabled={busy}><FaMagic /> {busy ? 'Preparando…' : 'Preparar'}</TechButton>
      </div>
      {err && <ErrorBox><FaExclamationTriangle /> {err}</ErrorBox>}
      {out && <Prep>{out}</Prep>}
      {!out && !busy && <Empty>Elegí tipo y tema, y la IA arma la preparación con todo el contexto de la plataforma.</Empty>}
    </div>
  );
}

function WarRoom() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await getWarroom()); } catch (e) { setError(friendlyError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (loading) return <Loading>Armando el plan del día…</Loading>;
  if (error) return <ErrorBox><FaExclamationTriangle /> {error}</ErrorBox>;
  const PCOL = { alta: '#ef4444', media: '#f59e0b' };
  const s = data?.señales || {};
  const degradado = Array.isArray(data?.degradado) ? data.degradado : [];
  const fmt = (v) => (Array.isArray(v) ? v.length : (v ?? 0));

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
        <StatusPill>{data?.plan?.length || 0} acciones</StatusPill>
        <TechButtonGhost onClick={load}><FaSyncAlt /> Regenerar</TechButtonGhost>
      </div>
      {degradado.length > 0 && (
        <div style={{ border: '1px solid #f59e0b', borderRadius: 8, padding: '0.5rem 0.8rem', marginBottom: '0.6rem', background: 'rgba(245,158,11,0.08)', color: '#f59e0b', fontSize: '0.8rem', display: 'flex', alignItems: 'center', gap: 8 }}>
          <FaExclamationTriangle /> Señales no disponibles ({degradado.join(', ')}); pueden verse en 0 sin ser reales.
        </div>
      )}
      {data?.resumen && <div style={{ border: `1px solid ${borderSoft}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: '0.8rem 1rem', marginBottom: '0.8rem', fontSize: '0.9rem' }}>{data.resumen}</div>}

      {/* Estadísticas que usó el war room */}
      <StatStrip>
        <StatTile><StatLabel>Contenido pendiente</StatLabel><StatValue>{fmt(s.contenido_pendiente)}</StatValue><StatSub>a aprobar</StatSub></StatTile>
        <StatTile><StatLabel>Reclamos detectados</StatLabel><StatValue>{fmt(s.reclamos_detectados)}</StatValue><StatSub>{fmt(s.reclamos_resueltos)} resueltos</StatSub></StatTile>
        <StatTile><StatLabel>Barrios sin actividad</StatLabel><StatValue>{fmt(s.barrios_sin_actividad)}</StatValue><StatSub>oportunidad territorial</StatSub></StatTile>
        <StatTile><StatLabel>Medios oportunidad</StatLabel><StatValue>{fmt(s.medios_oportunidad)}</StatValue><StatSub>cubren a Passaglia, a vos no</StatSub></StatTile>
        <StatTile><StatLabel>Circuitos</StatLabel><StatValue>{fmt(s.circuitos_cargados)}</StatValue><StatSub>con datos electorales</StatSub></StatTile>
      </StatStrip>

      {(data?.plan || []).map((a, i) => {
        const route = SECCION_ROUTE[a.seccion];
        const isOpen = open === i;
        return (
          <Card key={i} style={{ borderLeft: `3px solid ${PCOL[a.prioridad] || accent}`, cursor: 'pointer' }} onClick={() => setOpen(isOpen ? null : i)}>
            <h5>{i + 1}. {a.accion} <TechChip>{a.seccion}</TechChip> <TechChip style={{ color: PCOL[a.prioridad], borderColor: PCOL[a.prioridad] }}>{a.prioridad}</TechChip></h5>
            <p>{a.por_que}</p>
            {isOpen && (
              <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: `1px solid ${borderSoft}` }}>
                {a.seccion === 'Territorio' && Array.isArray(s.barrios_sin_actividad) && s.barrios_sin_actividad.length > 0 &&
                  <p><b style={{ color: accent }}>Barrios sin actividad:</b> {s.barrios_sin_actividad.join(' · ')}</p>}
                {a.seccion === 'Prensa' && Array.isArray(s.medios_oportunidad) && s.medios_oportunidad.length > 0 &&
                  <p><b style={{ color: accent }}>Medios a trabajar:</b> {s.medios_oportunidad.join(' · ')}</p>}
                {route && (
                  <TechButton onClick={(e) => { e.stopPropagation(); navigate(route); }}>
                    Ir a {a.seccion} <FaArrowRight />
                  </TechButton>
                )}
              </div>
            )}
          </Card>
        );
      })}
      {(data?.plan || []).length === 0 && <Empty>Sin acciones sugeridas.</Empty>}
      <p style={{ fontFamily: mono, fontSize: '0.72rem', color: 'var(--text-secondary)', marginTop: '0.6rem' }}>
        Tocá una acción para ver los datos detrás y saltar a la sección donde ejecutarla.
      </p>
    </div>
  );
}

export default function Estrategia() {
  const [tab, setTab] = useState('plan');
  return (
    <TechShell>
      <TechPageHeader
        title="Sala de Estrategia"
        dossier="WAR ROOM"
        subtitle="El plan de acción del día, qué empujan los rivales y tu contra-mensaje, más el preparador de sesiones, entrevistas y debates."
      />
      <TechTabs tabs={[{ id: 'plan', label: 'Plan del día' }, { id: 'narrativa', label: 'Narrativa rival' }, { id: 'prep', label: 'Preparador' }]} active={tab} onChange={setTab} />
      {tab === 'plan' ? <WarRoom /> : tab === 'narrativa' ? <Narrativa /> : <Preparador />}
    </TechShell>
  );
}
